// src/pages/customer/CustomerDebts.jsx
//
// Три экрана для клиента:
//   • MyCodeSheet   — личный QR: магазин сканирует его, чтобы записать долг на меня.
//                     Можно сохранить/отправить картинкой — товар везёт курьер,
//                     а код уходит в WhatsApp.
//   • CustomerDebts — мои долги по всем магазинам.
//   • CustomerDebtDetail — что именно брал (состав покупок, как накладная) и что платил.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { customerDebts as debtsApi } from '../../api';
import { useToast } from '../../hooks/useToast';
import { errorMessage } from '../../utils/errorMessage';
import { formatMoney } from '../../data/currencies';
// Готовые утилиты: работают и в вебе, и в нативном приложении.
// Свой <a download> в WebView НЕ работает — файл молча не сохраняется.
import { shareQR, saveQR, hasNativeShare } from '../../utils/qrShare';
import Icon from '../../components/Icon';
import BottomNav from '../../components/BottomNav';
import { CUSTOMER_NAV, useCustomerNav } from './CustomerHome';
import { useAuth } from '../../hooks/useAuth';
import { isValidWhatsapp } from '../../utils/whatsapp';

// ═══════════════════════════════════════════════════════════════
// ЛИЧНЫЙ КОД (шторка)
// ═══════════════════════════════════════════════════════════════
export function MyCodeSheet({ onClose }) {
  const { t } = useTranslation();
  const { show, ToastView } = useToast();
  const { user, updateProfile } = useAuth();
  const qrRef = useRef(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Без имени и WhatsApp код не выдаётся — сразу просим заполнить здесь же
  const [needProfile, setNeedProfile] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [whatsapp, setWhatsapp] = useState(user?.whatsapp || '');

  const loadCode = async () => {
    setLoading(true);
    try {
      setData(await debtsApi.myCode());
      setNeedProfile(false);
    } catch (e) {
      if (e?.data?.code === 'PROFILE_INCOMPLETE') setNeedProfile(true);
      else show(errorMessage(e, t), 'error');
    }
    setLoading(false);
  };

  useEffect(() => { loadCode(); }, []); // eslint-disable-line

  const saveProfile = async () => {
    if (!name.trim() || !isValidWhatsapp(whatsapp) || busy) return;
    setBusy(true);
    try {
      await updateProfile({ name: name.trim(), whatsapp: whatsapp.trim() });
      await loadCode();
    } catch (e) {
      show(errorMessage(e, t), 'error');
    }
    setBusy(false);
  };

  // Сохранить картинкой — те же утилиты, что в кассе (работают в WebView)
  const save = async () => {
    if (!qrRef.current || busy) return;
    setBusy(true);
    try {
      const svgEl = qrRef.current.querySelector('svg');
      await saveQR({ svgEl, filename: 'alsatuu-my-code.png' });
      show(t('myCode.saved'), 'success');
    } catch (e) {
      show(t('cashier.shareError'), 'error');
    }
    setBusy(false);
  };

  // Отправить — товар везёт курьер, код уходит магазину в WhatsApp
  const share = async () => {
    if (!qrRef.current || busy) return;
    setBusy(true);
    try {
      const svgEl = qrRef.current.querySelector('svg');
      const result = await shareQR({
        svgEl,
        filename: 'alsatuu-my-code.png',
        title: 'Alsatuu',
        text: t('myCode.shareText'),
      });
      if (result?.method === 'download-fallback') {
        show(t('cashier.qrSavedToDownloads'), 'success');
      }
    } catch (e) {
      show(t('cashier.shareError'), 'error');
    }
    setBusy(false);
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 200, display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', background: 'var(--bg)',
          borderRadius: '16px 16px 0 0', padding: 24,
          paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
          textAlign: 'center',
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 6, fontFamily: 'var(--font-display)' }}>
          {t('myCode.title')}
        </h3>
        <p style={{ fontSize: 13, color: 'var(--fg-muted)', marginBottom: 20, lineHeight: 1.5 }}>
          {t('myCode.hint')}
        </p>

        {loading ? (
          <p style={{ padding: 40, color: 'var(--fg-muted)' }}>{t('common.loading')}</p>
        ) : needProfile ? (
          <div style={{ textAlign: 'left' }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg)', background: 'var(--bg-subtle)', borderRadius: 'var(--r-sm)', padding: 12, marginBottom: 14, lineHeight: 1.5 }}>
              {t('myCode.needProfile')}
            </p>
            <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('auth.namePlaceholder')}</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} style={{ marginBottom: 8, marginTop: 4 }} />
            <label style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{t('debtors.whatsappRequired')}</label>
            <input className="input" type="tel" inputMode="tel" placeholder="+996 555 123 456" value={whatsapp}
              onChange={e => setWhatsapp(e.target.value)} style={{ marginBottom: 14, marginTop: 4 }} />
            <button className="btn btn-primary" onClick={saveProfile} disabled={!name.trim() || !isValidWhatsapp(whatsapp) || busy}>
              {busy ? '…' : t('myCode.showCode')}
            </button>
          </div>
        ) : data ? (

          <>
            <div
              ref={qrRef}
              style={{
                display: 'inline-block', padding: 16, background: '#fff',
                borderRadius: 12, marginBottom: 20,
              }}
            >
              <QRCodeSVG value={JSON.stringify(data.payload)} size={200} level="M" />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button className="btn btn-secondary" onClick={save} disabled={busy} style={{ flex: 1 }}>
                <Icon name="download" size={16} /> {t('myCode.save')}
              </button>
              <button className="btn btn-primary" onClick={share} disabled={busy} style={{ flex: 1 }}>
                <Icon name="share" size={16} /> {t('myCode.share')}
              </button>
            </div>

            <p style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.4 }}>
              {t('myCode.shareHint')}
            </p>
          </>
        ) : null}

        {ToastView}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// МОИ ДОЛГИ
// ═══════════════════════════════════════════════════════════════
export default function CustomerDebts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { show, ToastView } = useToast();
  const navItems = useCustomerNav();

  const [data, setData] = useState({ items: [], totalDebt: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setData(await debtsApi.list());
      } catch (e) {
        show(errorMessage(e, t), 'error');
      }
      setLoading(false);
    })();
  }, []); // eslint-disable-line

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('myDebts.subtitle')}</p>
          <h1 className="page-title">{t('myDebts.title')}</h1>
        </div>
      </div>

      {loading ? (
        <p style={{ padding: 24, color: 'var(--fg-muted)' }}>{t('common.loading')}</p>
      ) : data.items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '56px 24px' }}>
          <div style={{ opacity: 0.3, marginBottom: 12 }}>
            <Icon name="wallet" size={40} stroke={1.5} />
          </div>
          <p style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{t('myDebts.empty')}</p>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)', lineHeight: 1.5 }}>{t('myDebts.emptyHint')}</p>
        </div>
      ) : (
        <>
          {/* Общий долг */}
          <div className="card" style={{ marginBottom: 12, textAlign: 'center', padding: 20 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', marginBottom: 6 }}>
              {t('myDebts.total')}
            </p>
            <p style={{
              fontSize: 30, fontWeight: 800, fontFamily: 'var(--font-display)',
              color: data.totalDebt > 0 ? 'var(--danger)' : 'var(--success)',
            }}>
              {formatMoney(data.totalDebt, data.items[0]?.shop?.currency || 'KGS')}
            </p>
            {/* Магазины могут быть в разных странах — тогда сумма условна */}
            {new Set(data.items.map(i => i.shop?.currency).filter(Boolean)).size > 1 && (
              <p style={{ fontSize: 10, color: 'var(--fg-faint)', marginTop: 4 }}>
                {t('myDebts.mixedCurrencies')}
              </p>
            )}
          </div>

          {/* По магазинам */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {data.items.map(d => (
              <button
                key={d.counterpartyId}
                onClick={() => navigate(`/my-debts/${d.counterpartyId}`)}
                className="card"
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: 14, textAlign: 'left', cursor: 'pointer', border: 'none',
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 'var(--r-sm)', flexShrink: 0,
                  background: 'var(--bg-subtle)', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {d.shop?.photo
                    ? <img src={d.shop.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Icon name="store" size={20} color="var(--fg-muted)" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{d.shop?.name}</div>
                  {d.lastPurchaseAt && (
                    <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                      {t('myDebts.lastPurchase')}: {new Date(d.lastPurchaseAt).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div style={{
                  fontSize: 15, fontWeight: 800, flexShrink: 0,
                  color: d.debt > 0 ? 'var(--danger)' : 'var(--success)',
                }}>
                  {formatMoney(d.debt, d.shop?.currency || 'KGS')}
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {ToastView}
      <BottomNav items={navItems} />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ДЕТАЛИ: что брал и что платил
// ═══════════════════════════════════════════════════════════════
export function CustomerDebtDetail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cpId } = useParams();
  const { show, ToastView } = useToast();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSale, setOpenSale] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await debtsApi.get(cpId));
      } catch (e) {
        show(errorMessage(e, t), 'error');
        navigate('/my-debts', { replace: true });
      }
      setLoading(false);
    })();
  }, [cpId]); // eslint-disable-line

  if (loading) {
    return <div className="page"><p style={{ padding: 24, color: 'var(--fg-muted)' }}>{t('common.loading')}</p></div>;
  }
  if (!data) return null;

  // У каждого магазина своя валюта (сом / тенге / сум)
  const money = (v) => formatMoney(v, data.shop?.currency || 'KGS');

  return (
    <div className="page">
      <div className="page-header">
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 8, marginLeft: -8, cursor: 'pointer', color: 'var(--fg-muted)' }}
          aria-label={t('common.back')}
        >
          <Icon name="chevronLeft" size={22} stroke={2.5} />
        </button>
        <div style={{ flex: 1 }}>
          <p className="page-subtitle">{t('myDebts.subtitle')}</p>
          <h1 className="page-title">{data.shop?.name}</h1>
        </div>
      </div>

      {/* Текущий долг */}
      <div className="card" style={{ marginBottom: 12, textAlign: 'center', padding: 18 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', marginBottom: 4 }}>
          {t('myDebts.currentDebt')}
        </p>
        <p style={{
          fontSize: 26, fontWeight: 800, fontFamily: 'var(--font-display)',
          color: data.debt > 0 ? 'var(--danger)' : 'var(--success)',
        }}>
          {money(data.debt)}
        </p>
      </div>

      {/* Покупки — с составом, как накладная */}
      {data.sales.length > 0 && (
        <>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', margin: '4px 0 8px' }}>
            {t('myDebts.purchases')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {data.sales.map(s => {
              const open = openSale === s.id;
              return (
                <div key={s.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <button
                    onClick={() => setOpenSale(open ? null : s.id)}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                      padding: 14, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {new Date(s.createdAt).toLocaleString()}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                        {s.items.length} {t('wholesale.itemsCount')}
                        {s.creditAmount > 0 && ` · ${t('myDebts.onCredit')}: ${money(s.creditAmount)}`}
                      </div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, flexShrink: 0 }}>{money(s.total)}</div>
                    <Icon name={open ? 'chevronDown' : 'chevronRight'} size={16} color="var(--fg-muted)" />
                  </button>

                  {open && (
                    <div style={{ padding: '0 14px 14px', borderTop: '1px solid var(--border)' }}>
                      {s.items.map((i, k) => (
                        <div key={k} style={{
                          display: 'flex', justifyContent: 'space-between', gap: 8,
                          padding: '8px 0', borderBottom: k < s.items.length - 1 ? '1px solid var(--border)' : 'none',
                        }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13 }}>{i.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                              {i.qty} {i.unit} × {money(i.price)}
                            </div>
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{money(i.total)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Платежи */}
      {data.payments.length > 0 && (
        <>
          <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--fg-muted)', letterSpacing: '0.06em', margin: '4px 0 8px' }}>
            {t('myDebts.payments')}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.payments.map(p => (
              <div key={p.id} className="card" style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 12,
              }}>
                <div>
                  <div style={{ fontSize: 13 }}>{t(`cpPayments.method_${p.method}`)}</div>
                  <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
                    {new Date(p.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--success)' }}>
                  −{money(p.amount)}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {ToastView}
    </div>
  );
}
